import { useId } from "react";
import { motion, useReducedMotion } from "framer-motion";

const draw = {
  hidden: { pathLength: 0, opacity: 0 },
  show: { pathLength: 1, opacity: 1, transition: { pathLength: { duration: 0.9, ease: [0.65, 0, 0.35, 1] }, opacity: { duration: 0.1 } } },
};
const wash = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { delay: 0.55, duration: 0.4 } },
};

/* Hand-drawn ink icon set for Mirai Educations.
   Ink strokes use currentColor; the "accent" (default brand gold) is a
   slightly off-register fill or stroke, like a marker swatch behind a pen sketch. */

const A = ({ d }) => <motion.path variants={wash} d={d} style={{ fill: "var(--ink-accent)", stroke: "none" }} />;
const As = ({ d }) => <motion.path variants={draw} d={d} style={{ stroke: "var(--ink-accent)" }} strokeWidth={2.2} />;
const Dot = ({ cx, cy, r = 1.4 }) => <motion.circle variants={wash} cx={cx} cy={cy} r={r} style={{ fill: "var(--ink-accent)", stroke: "none" }} />;
const I = ({ d }) => <motion.path variants={draw} d={d} />;

const Svg = ({ className = "h-6 w-6", accent = "#F5A524", children }) => {
  const id = useId().replace(/:/g, "");
  const reduce = useReducedMotion();
  return (
    <motion.svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
      initial={reduce ? false : "hidden"} whileInView="show" viewport={{ once: true, amount: 0.1 }}
      className={className} style={{ "--ink-accent": accent }} aria-hidden="true" focusable="false">
      <defs>
        <filter id={`ink-${id}`} x="-10%" y="-10%" width="120%" height="120%">
          <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="2" seed="7" />
          <feDisplacementMap in="SourceGraphic" scale="0.7" />
        </filter>
      </defs>
      <g filter={`url(#ink-${id})`}>{children}</g>
    </motion.svg>
  );
};

const make = (body) => (props) => <Svg {...props}>{body}</Svg>;

export const InkLaptop = make(<>
  <A d="M8.5 9.5c3-.8 10-1 14.6-.2.6 2.6.4 6.4-.3 8.6-4.4.6-10 .5-13.8-.1-.7-2.6-.9-6-.5-8.3z" />
  <I d="M7.2 21.4c-.3-4.2-.4-9.6.1-13.6 5.6-.6 12.4-.7 17.8.1.4 4 .3 9.2-.1 13.4" />
  <I d="M3.6 22.2c7.9-.5 16.6-.6 24.9.1-.4 1.6-1.4 2.6-3 2.8-6.4.4-12.7.4-18.9 0-1.6-.3-2.6-1.3-3-2.9z" />
  <I d="M14 22.8h4.2" />
</>);

export const InkClock = make(<>
  <A d="M18.4 6.6c4.3.2 7.4 3.6 7 7.9-.4 4.1-4 6.9-8 6.4-3.8-.5-6.2-3.9-5.8-7.6.4-3.9 3.2-6.9 6.8-6.7z" />
  <I d="M16.9 4.8c6.3-.3 11 4.6 10.6 10.9-.4 6.1-5.6 10.7-11.6 10.3C9.8 25.6 5 20.6 5.3 14.6 5.6 9.4 9.4 5.7 14.2 5.1" />
  <I d="M16.2 9.6c-.2 2.2-.1 4.3.2 6.3l4.1 2.4" />
</>);

export const InkBriefcase = make(<>
  <A d="M6.5 14c5.8.9 13.4.9 19.3-.2l-.3 3.2c-5.9.9-12.8.9-18.7.1z" />
  <I d="M4.8 11.6c7.5-.7 15.1-.7 22.5 0 .4 4.2.4 8.5-.1 12.6-7.4.6-14.9.6-22.3 0-.5-4.1-.5-8.4-.1-12.6z" />
  <I d="M12 11.3c-.2-1.9.1-3.5.6-4.4 2.1-.5 4.6-.5 6.8 0 .5.9.7 2.6.5 4.4" />
  <I d="M14.6 17.2c1-.2 2-.2 2.9 0" />
</>);

export const InkGrowth = make(<>
  <As d="M4.6 26.4c6.5-1 15.2-1.4 23-.6" />
  <I d="M4.6 21.8c2.6-2.7 5-5.3 7.5-7.6 1.7 1.6 3.2 3.1 4.9 4.6 3.1-3.4 6.4-6.8 10-10.1" />
  <I d="M21.4 8.2c2-.3 4-.4 5.9-.4.1 1.8 0 3.9-.3 5.8" />
</>);

export const InkCap = make(<>
  <A d="M9.6 15.8c.1 2.1.2 4 .6 5.6 3.6 1.6 7.8 1.7 11.4 0 .4-1.7.5-3.6.5-5.6-4.3 1.6-8.5 1.5-12.5 0z" />
  <I d="M3.6 12.6c4-1.9 8.2-3.6 12.5-5 4.3 1.3 8.6 3 12.5 4.9-4 2.2-8.2 4-12.5 5.4-4.3-1.3-8.5-3.1-12.5-5.3z" />
  <I d="M9.4 15.4c-.1 2 0 4.2.6 6.2 3.8 1.9 8.4 1.9 12.1-.1.5-2 .6-4 .5-6.1" />
  <I d="M27.6 12.8c.1 2.2.1 4.4-.2 6.6M27.2 19.6l.5 2.4" />
</>);

export const InkBook = make(<>
  <A d="M17 9.2c2.6-1.4 6-2 9-1.6.3 4.5.2 9.4-.1 13.6-3.1-.3-6.2.3-8.8 1.8-.3-4.6-.3-9.3-.1-13.8z" />
  <I d="M16 9.3c-2.9-1.7-6.6-2.3-10.1-1.9-.4 4.7-.4 9.5.1 14.1 3.6-.3 7.1.4 10 2.1" />
  <I d="M16 9.3c2.9-1.7 6.6-2.3 10.1-1.9.4 4.7.4 9.5-.1 14.1-3.6-.3-7.1.4-10 2.1" />
  <I d="M16 9.4c-.2 4.8-.2 9.6 0 14.3" />
  <I d="M8.6 12.2c1.6 0 3.1.3 4.6.8M8.6 15.8c1.6 0 3.1.3 4.6.8" />
</>);

export const InkAward = make(<>
  <A d="M16.2 5.8c3.4 0 5.8 2.5 5.7 5.7-.1 3.1-2.7 5.5-5.9 5.4-3.1-.1-5.4-2.6-5.3-5.6.1-3.1 2.4-5.5 5.5-5.5z" />
  <I d="M16.6 4c4.3.1 7.6 3.4 7.4 7.6-.2 4.1-3.7 7.2-7.9 7-4.1-.2-7.1-3.6-6.9-7.6.2-3.8 3.1-6.7 6.7-7" />
  <I d="M11.8 17.2c-.7 3.1-1.4 6.2-2.4 9.2 1.4-.6 2.8-.9 4.2-1 .7 1 1.4 2.1 2 3.2.6-2.6 1-5.1 1.3-7.6" />
  <I d="M20.4 17.4c.6 3 1.3 6 2.2 9-1.3-.5-2.7-.8-4.1-.9" />
  <I d="M13.6 11.4c.9.9 1.6 1.6 2.4 2.4 1.2-1.6 2.4-3 3.8-4.2" />
</>);

export const InkPeople = make(<>
  <A d="M19.8 7.6c2.2 0 3.7 1.7 3.6 3.8-.1 2-1.8 3.5-3.8 3.4-1.9-.1-3.3-1.8-3.2-3.7.1-2 1.5-3.5 3.4-3.5z" />
  <I d="M12.3 6.4c2.6-.1 4.5 2 4.4 4.5-.1 2.5-2.2 4.4-4.7 4.3-2.4-.1-4.2-2.2-4-4.6.2-2.3 2-4.1 4.3-4.2z" />
  <I d="M4.4 25.6c.2-4.6 3.5-7.6 7.8-7.7 4.4-.1 7.6 3 7.9 7.6" />
  <I d="M20.8 17.6c3.6.1 6.4 2.8 6.8 6.8M20.6 7.4c2.2.1 3.7 2 3.5 4.2-.2 1.8-1.5 3.2-3.2 3.5" />
</>);

export const InkScale = make(<>
  <A d="M4.6 17.8c1.4 2.4 5.6 2.5 7-.1z" />
  <A d="M20.4 17.8c1.3 2.4 5.7 2.5 7-.1z" />
  <I d="M16 5.6c.2 6.6.2 13.2-.1 19.8M10.6 26c3.6-.4 7.4-.4 11 .1" />
  <I d="M6.4 9.4c3.2-.8 6.4-1.3 9.6-1.4 3.3 0 6.5.4 9.6 1.3" />
  <I d="M8.2 9.6c-1.4 2.6-2.6 5.2-3.6 8 2.4.3 4.8.3 7.1 0-1-2.8-2.2-5.4-3.5-8" />
  <I d="M24 9.6c-1.4 2.6-2.6 5.2-3.6 8 2.4.3 4.8.3 7.1 0-1-2.8-2.2-5.4-3.5-8" />
</>);

export const InkDocument = make(<>
  <A d="M10.4 16.4c3.8-.4 7.6-.4 11.4 0l-.1 2.6c-3.8.3-7.5.3-11.2 0z" />
  <I d="M8 4.8c3.8-.3 7.6-.3 11.3 0 1.9 1.7 3.6 3.6 5 5.6.4 5.6.4 11.2-.1 16.8-5.4.4-10.9.4-16.2 0-.5-7.4-.5-15 0-22.4z" />
  <I d="M19 5c-.2 1.9-.1 3.7.2 5.4 1.7.2 3.4.2 5.1 0" />
  <I d="M11.2 13.6c2 0 4 0 6 .1M11.2 17.8c3.2-.1 6.5-.1 9.6 0M11.2 21.8c2.4 0 4.7 0 7 .1" />
</>);

export const InkShield = make(<>
  <A d="M16.4 7.2c2.6 1.1 5.4 1.9 8.2 2.3.1 6.6-3.1 11.5-8.2 14.4z" />
  <I d="M16 3.8c-3 1.6-6.4 2.5-10 2.9-.3 9 3.5 16 10 20.6 6.5-4.6 10.3-11.6 10-20.6-3.6-.4-7-1.3-10-2.9z" />
  <I d="M11.4 15.6c1.3 1.1 2.4 2.2 3.4 3.5 2-3 4.2-5.6 6.6-7.8" />
</>);

export const InkHeart = make(<>
  <A d="M9.6 8.6c2-.2 3.6 1 4.6 2.8-.4 2.6-2.8 4.6-5.6 4.6-1.6-1.6-2-3.6-1.4-5.4.5-1.2 1.3-1.9 2.4-2z" />
  <I d="M16 26c-5.4-3.6-11-8.2-11.2-13.8-.1-3.6 2.4-6.3 5.6-6.4 2.4 0 4.2 1.4 5.6 3.6 1.4-2.2 3.3-3.6 5.7-3.6 3.2.1 5.6 2.9 5.4 6.4-.3 5.6-5.8 10.2-11.1 13.8z" />
  <As d="M25.4 2.8l.5 2.2M28.6 5.6l-2 .9" />
</>);

export const InkChat = make(<>
  <I d="M16.4 5c6.2-.1 11 3.9 11 9.2.1 5.2-4.8 9.4-11 9.4-1.6 0-3.1-.2-4.5-.7-2.2 1.4-4.6 2.3-7.2 2.6 1.1-1.5 1.9-3.1 2.2-4.8-1.6-1.6-2.5-3.6-2.5-5.8C4.3 9 9.9 5.1 16.4 5z" />
  <Dot cx={11.6} cy={14.4} /><Dot cx={16.2} cy={14.2} /><Dot cx={20.8} cy={14.5} />
</>);

export const InkPhone = make(<>
  <As d="M19.4 5.4c3.4.6 6 3.2 6.8 6.6M18.8 9.4c1.6.4 2.8 1.6 3.2 3.2" />
  <I d="M8.4 4.8c1.2-.3 2.4-.4 3.6-.4.9 2.2 1.6 4.4 2 6.6-.9.7-1.9 1.3-2.9 1.8 1.6 3.6 4.4 6.6 8 8.4.5-1 1.1-2 1.8-2.9 2.3.4 4.5 1 6.7 1.9 0 1.2-.2 2.5-.5 3.7-.4 1.2-1.4 2-2.6 2.1C13.6 26.1 5.9 18.4 6 9.5c.1-2.2.9-4.2 2.4-4.7z" />
</>);

export const InkClipboard = make(<>
  <A d="M11.6 4.6c2.9-.3 5.9-.3 8.8 0 .3 1 .3 2.2 0 3.2-2.9.3-5.9.3-8.8 0-.3-1-.3-2.2 0-3.2z" />
  <I d="M9.6 6.8c-1.4 0-2.4.4-2.8.8-.4 6.4-.4 12.8 0 19.2 6 .5 12.2.5 18.4 0 .4-6.4.4-12.8 0-19.2-.5-.4-1.5-.8-2.8-.8" />
  <I d="M11.6 4.6c2.9-.3 5.9-.3 8.8 0 .3 1 .3 2.2 0 3.2-2.9.3-5.9.3-8.8 0-.3-1-.3-2.2 0-3.2z" />
  <I d="M11.4 17.2c1.2 1 2.3 2.1 3.2 3.3 2-2.8 4.1-5.2 6.4-7.2" />
</>);

export const InkWallet = make(<>
  <A d="M6.4 21.4c6.4.4 13 .4 19.4 0l-.1 2.4c-6.4.3-12.9.3-19.2 0z" />
  <I d="M5.2 9.6c7.3-.5 14.6-.5 21.8 0 .4 5.3.4 10.6 0 15.9-7.2.5-14.5.5-21.8 0-.5-5.3-.5-10.6 0-15.9z" />
  <I d="M6 9.4c4.8-1.9 9.8-3.6 14.8-4.8.7 1.5 1.1 3.1 1.3 4.8" />
  <I d="M27 14.4c-2-.2-4-.2-6 0-.4 1.5-.4 3 0 4.5 2 .2 4 .2 6 0" />
  <Dot cx={23.4} cy={16.7} r={1.2} />
</>);

export const InkReceipt = make(<>
  <A d="M11 13.2c3.4-.3 6.7-.3 10 0l-.1 2.8c-3.3.2-6.6.2-9.8 0z" />
  <I d="M8 4.6c5.3-.3 10.6-.3 16 0 .3 7.6.3 15.2 0 22.8l-2.6-1.6-2.7 1.8-2.7-1.8-2.7 1.8-2.6-1.8L8 27.4c-.4-7.6-.4-15.2 0-22.8z" />
  <I d="M11.6 10.2c2.9-.1 5.9-.1 8.8 0M11.6 14.6c2.9-.1 5.9-.1 8.8 0M11.6 19c1.8 0 3.6 0 5.4.1" />
</>);

export const InkSearch = make(<>
  <A d="M15.2 7.4c3.2.2 5.4 2.8 5.2 5.9-.2 3-2.8 5.2-5.8 5-3-.2-5.1-2.7-4.9-5.6.2-3.1 2.6-5.4 5.5-5.3z" />
  <I d="M14.4 5.2c4.6-.1 8.2 3.4 8.1 7.9-.1 4.4-3.8 7.9-8.3 7.8-4.4-.1-7.8-3.7-7.6-8.1.1-3.8 2.8-6.9 6.4-7.5" />
  <I d="M20.4 19.4c2.3 2.2 4.5 4.5 6.6 6.9" />
</>);

export const InkIdCard = make(<>
  <A d="M5 7.8c7.4-.3 14.8-.3 22.2 0l.1 2.6c-7.5.3-15 .3-22.4 0z" />
  <I d="M4.6 7.6c7.6-.4 15.2-.4 22.8 0 .4 5.6.4 11.2 0 16.8-7.6.4-15.2.4-22.8 0-.4-5.6-.4-11.2 0-16.8z" />
  <I d="M11.4 12.6c1.5 0 2.6 1.2 2.5 2.6 0 1.4-1.2 2.5-2.6 2.4-1.4 0-2.4-1.2-2.4-2.6.1-1.3 1.1-2.4 2.5-2.4z" />
  <I d="M7.8 21.4c.6-1.8 2-2.8 3.6-2.8s3 1 3.6 2.8M18 14c1.8-.1 3.6-.1 5.4 0M18 18c1.2 0 2.4 0 3.6.1" />
</>);

export const InkCheck = make(<>
  <I d="M8 16.6c2.2 1.8 4 3.6 5.6 5.6 3.4-5 7-9.2 11-12.6" />
</>);
